import {delay, texture} from "p3";
import { Rectangle } from "pixi.js-legacy";
import common from "../common";
import definitions from "../definitions";
import BossFactory from "../game/boss/BossFactory";
import BossBert from "../game/boss/player/BossBert";
import BossIvandoe from "../game/boss/player/BossIvandoe";
import HUD from "../ui/boss/HUD";
import { locale } from "../utils";
import Scene from "./Scene";

export default class BossFightScene extends Scene {
    /**
     * @param {string} bossId
     */
    constructor(bossId) {
        super("boss");

        this._betweenTurnsDelay = 1;
        this._bossAttackDelay = 0.25;

        this._bossId = bossId;
        this._currentDamageDelt = 0;

        this._defaultTween = null;
        this._cameraIdleTween = null;
        this._floatingTextTween = null;

        this._boss = null;
        this._ivandoe = null;
        this._bert = null;

        this._background = null;
        this._hud = null;
        this._camera = null;
        this._cameraTarget = null;
        this._sceneLayer = null;
        this._foregroundLayer = null;
        this._actionWheelHitBox = null;
        this._actionWheelHitBoxContainer = null;
        this._bgm = null;

        this._endTransitionDelay = 1;
    }

    init() {
        super.init();
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        if (!common.saveData.settings.musicMute) {
            this._bgm = P3.audio.playSound("music_boss_fight_loop", {loop:true});
        }
        this._sceneLayer = new PIXI.Container();
        this.addChild(this._sceneLayer);

        this._foregroundLayer = new PIXI.Container();
   
        this._createBackground();

        this._actionWheelHitBoxContainer = new PIXI.Container();
        this._actionWheelHitBoxContainer.interactive = true;
        this._actionWheelHitBoxContainer.hitArea = new Rectangle(0, 0, common.STAGE_WIDTH, P3.View.height);
        this.addChild(this._actionWheelHitBoxContainer);

        this._createBoss();
        this._createPlayer();

        this.addChild(this._foregroundLayer);

        this._createUI();

        this._camera = new P3.Camera(new PIXI.Point(common.STAGE_WIDTH * 0.5, P3.View.height * 0.5), new PIXI.Point(-common.STAGE_WIDTH * 0.25, common.STAGE_HEIGHT * 0.2));
        this._camera.addLayer(new P3.CameraLayer(this._sceneLayer, new PIXI.Point(1, 1)));
        this._camera.addLayer(new P3.CameraLayer(this._foregroundLayer, new PIXI.Point(1, 1)));
        this._camera.zoom = 1;
        this._cameraTarget = new PIXI.Point(0, 0);
        this._camera.setTarget(this._cameraTarget);

        this._onBegin();
    }

    update() {
        super.update();

        this._boss && this._boss.update();
        this._ivandoe && this._ivandoe.update();
        this._bert && this._bert.update();
        this._hud && this._hud.update();
        this._camera && this._camera.update();
    }

    resize() {
        super.resize();

        this._hud && this._hud.resize();

        if (this._actionWheelHitBoxContainer) {
            this._actionWheelHitBoxContainer.hitArea = new Rectangle(0, 0, common.STAGE_WIDTH, P3.View.height);
        }
    }

    _createBackground() {
        switch (this._bossId) {
            case definitions.BOSS_ID.GIANT_SPIDER: {
                    const bg = new PIXI.Sprite(texture("spider_cave_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.position.set(-common.STAGE_WIDTH / 2, -common.STAGE_HEIGHT / 2);
                    this._sceneLayer.addChild(bg);
                }
                break;
            case definitions.BOSS_ID.CHICK_OF_DOOM: {
                    const bg = new PIXI.Sprite(texture("chicken_farm_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);

                    const foregroundBg = new PIXI.Sprite(texture("foreground_farm"));
                    foregroundBg.position.set(-common.STAGE_WIDTH * 0.5, 0);
                    this._foregroundLayer.addChild(foregroundBg);
                }
                break;
            case definitions.BOSS_ID.BATWITCH: {
                    const bg = new PIXI.Sprite(texture("batwitch_forest_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);

                    const foregroundBg = new PIXI.Sprite(texture("foreground_forest"));
                    foregroundBg.position.set(-common.STAGE_WIDTH * 0.5, 100);
                    this._foregroundLayer.addChild(foregroundBg);
                }
                break;
            case definitions.BOSS_ID.SUZIE: {
                    const bg = new PIXI.Sprite(texture("suzie_swamp_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.position.set(-common.STAGE_WIDTH / 2, -common.STAGE_HEIGHT / 2);
                    this._sceneLayer.addChild(bg);
                    
                    const foregroundBg = new PIXI.Sprite(texture("foreground_swamp"));
                    foregroundBg.position.set(-common.STAGE_WIDTH * 0.5, 0);
                    this._foregroundLayer.addChild(foregroundBg);
                }
                break;
        }
    }

    _shakeCamera() {}

    _createBoss() {
        this._boss = BossFactory.create(this._bossId, {});
        this._boss.init();

        if (this._boss) {
            this._boss.position.set(common.STAGE_WIDTH * 0.7, common.STAGE_HEIGHT * 0.2);
            this._boss.scale.set(-1, 1);
            this._boss.signals.attackEnd.add(() => delay(this._betweenTurnsDelay, () => this._onStartPlayerAttack()), this);
            this._boss.signals.dead.add(this._onComplete, this);
            this._boss.signals.bossStrike.add(this._onEnemyAttack, this);
        }

        this._sceneLayer.addChild(this._boss);
    }

    _createPlayer() {
        this._ivandoe = new BossIvandoe();
        this._ivandoe.init();

        this._bert = new BossBert();
        this._bert.init();

        if (this._ivandoe) {
            this._ivandoe.position.set(-common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.2);
            this._ivandoe.signals.dead.add(this._onFail, this);
            this._ivandoe.signals.damageDelt.add(() => {
                this._hud.bossHealthBar.health = Math.max(0, this._hud.bossHealthBar.health - this._currentDamageDelt);

                this._shake(-70);
    
                if (this._currentDamageDelt > 0) {
                    this._spawnFloatingText(locale('ui_boss_hit'), 0);
    
                    P3.audio.playSound("sfx_ivandoe_hit_enemy");
    
                    this._boss.takeDamage(this._currentDamageDelt);
                } else {
                    this._spawnFloatingText(locale('ui_boss_miss'), 0);
                }
            }, this);
        }

        if (this._bert) this._bert.position.set(-common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.2);

        this._sceneLayer.addChild(this._ivandoe);
        this._sceneLayer.addChild(this._bert);
    }

    _createUI() {
        this._hud = new HUD(this._ivandoe, this._boss);
        this._hud.signals.attack.add((damage) => this._onPlayerAttack(damage), this);
        this._hud.signals.heal.add(() => this._onPlayerHeal(), this);
        this._hud.signals.miss.add(() => this._onPlayerAttack(0), this);
        this._hud.pauseButton.signals.click.add(this.onPauseButtonClick, this);
        this._hud.surrenderButton.signals.click.add(this.onSurrenderButtonClick, this);
        this.addChild(this._hud);
        
        this._actionWheelHitBoxContainer.on("pointerdown", () => this._hud.attackBar.onMouseClick());
    }

    onSurrenderButtonClick(button) {
        this._hud.attackBar.animateOut().then(() => {
            this._defaultTween = gsap.timeline();
            this.addTween(this._defaultTween);
    
            this._ivandoe.playGiveInAnimation();
            
            this._defaultTween.add(gsap.to(this._ivandoe.position, 5, {
                x: -common.STAGE_WIDTH * 0.5,
                y: this._ivandoe.y,
                ease: Power0.easeInOut,
                onStart: () => {
                    this._ivandoe.facingDirection = -1;
                }
            }), 0);
    
            this._defaultTween.add(gsap.to(this._bert.position, 5, {
                x: -common.STAGE_WIDTH * 0.5,
                y: this._bert.y,
                ease: Power0.easeInOut,
                onStart: () => {
                    this._bert.facingDirection = -1;
                }
            }), 0);
    
            this.delay(1).then(() => this.signals.next.dispatch(false));
        });
    }

    /**
     * @param {P3.Button} button 
     */
    onPauseButtonClick(button) {
        this.signals.pause.dispatch();
    }

    _onStartPlayerAttack() {
        if(this._ivandoe.isDead) {
            return;
        }

        this._zoomOutFromFocus().then(() => 
        {
            this._camera.focus.set(-common.STAGE_WIDTH * 0.25, common.STAGE_HEIGHT * 0.2);

            this._zoomInToFocus().then(() => {
                this._hud.attackBar.animateIn().then(() => {
                    this._hud.attackBar.enabled = true;
                });
            });
        });
    }

    _onPlayerHeal() { 
        this._hud.attackBar.animateOut();

        this._defaultTween = gsap.timeline();
        this.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._bert.position, 1.5, {
            x: this._ivandoe.position.x - 50,
            y: this._ivandoe.position.y - 50,
            ease: Power1.easeInOut,
            yoyo: true,
            repeat: 1,
            onStart: () => {
                P3.audio.playSound("vo_bert_heal_00");
                this._bert.signals.healIvandoe.dispatch();
            },
            onRepeat: () => {
                this._bert.playIdleAnimation(false);
                this._ivandoe.playHealAnimation();

                this._spawnFloatingText(locale('ui_boss_healed'), -common.STAGE_WIDTH * 0.15);

                if (this._ivandoe.health < this._ivandoe.maxHealth) {
                    this._ivandoe.health = this._ivandoe.health + 6;

                    if (this._ivandoe.health >= this._ivandoe.maxHealth) {
                        this._ivandoe.health = this._ivandoe.maxHealth;
                    }

                    this._hud.playerHealthBar.health = Math.max(0, this._hud.playerHealthBar.health + 6);        
                }
            },
            onComplete: () => this._onStartEnemyAttack()
        }), 0);
    }

    /**
     * @param {number} damage 
     */
    _onPlayerAttack(damage) {
        this._currentDamageDelt = damage;

        this._hud.attackBar.animateOut().then(() => {
            this._ivandoe.signals.attack.dispatch();

            delay(this._betweenTurnsDelay, () => this._onStartEnemyAttack());
        });
    }

    /**
     * @param {number} offset 
     */
    _shake(offset) {
        this._defaultTween = gsap.timeline();
        this.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._cameraTarget, 0.1, {
            x: this._cameraTarget.x + offset,
            y: this._cameraTarget.y,
            yoyo: true,
            repeat: 1,
            ease: Circ.easeInOut,
            onComplete: () => {}
        }), 0);
    }

    _zoomInToFocus() {
        return new Promise((resolve) => {
            this._defaultTween = gsap.timeline();
            this.addTween(this._defaultTween);
    
            this._defaultTween.add(gsap.to(this._camera, 0.35, {
                zoom: 1.35,
                ease: Power1.easeInOut,
                onComplete: () => {
                    resolve();
                }
            }), 0);
        });
    }

    _zoomOutFromFocus() {
        this.removeTween(this._cameraIdleTween);

        return new Promise((resolve) => {
            this._defaultTween = gsap.timeline();
            this.addTween(this._defaultTween);
    
            this._defaultTween.add(gsap.to(this._camera, 0.35, {
                zoom: 1.0,
                ease: Power1.easeInOut,
                onComplete: () => {
                    resolve();
                }
            }), 0);
        });
    }

    _onEnemyAttack() {
        const damage = this._boss.getDamageAmount();

        this._shake(70);
        
        if (damage > 0) {
            this._spawnFloatingText(locale('ui_boss_hit'), -common.STAGE_WIDTH * 0.15);
            
            this._ivandoe.signals.takeDamage.dispatch(damage);

            this._hud.playerHealthBar.health = Math.max(0, this._hud.playerHealthBar.health - damage);
        } else {
            this._spawnFloatingText(locale('ui_boss_miss'), -common.STAGE_WIDTH * 0.15);
        }
    }

    _onStartEnemyAttack() {
        if (this._boss.isDead) {
            return;
        }

        this._zoomOutFromFocus().then(() => {
            this._camera.focus.set(common.STAGE_WIDTH * 0.12, common.STAGE_HEIGHT * 0.2);
    
            this._hud.attackBar.animateOut().then(() => {
                this._zoomInToFocus().then(() => {      
                    this.delay(this._bossAttackDelay).then(() => {
                        this._boss.state = definitions.BOSS_STATE.ATTACK;
                    });
                });
            });
        });
    }

    /**
     * @param {string} text 
     * @param {number} x 
     */
    _spawnFloatingText(text, x) {
        let action = new PIXI.BitmapText(text, {
            fontName: "Alghera Pro",
            fontSize: 50,
            align: "center",
            tint: 0xFFFFFF
        });
        action.alpha = 1;
        action.position.set(x, 0);
        action.anchor.set(0.5);
        action.scale.set(0);
        this._sceneLayer.addChild(action);

        this._floatingTextTween = gsap.timeline();
        this.addTween(this._floatingTextTween);

        this._floatingTextTween.add(gsap.to(action.position, 1.5, {
            x: x,
            y: -common.STAGE_HEIGHT * 0.1,
            ease: Cubic.easeOut,
            onComplete: () => {}
        }), 0);

        this._floatingTextTween.add(gsap.to(action, 1.5, {
            alpha: 0,
            ease: Cubic.easeOut,
            onComplete: () => {}
        }), 0);

        this._floatingTextTween.add(gsap.to(action.scale, 0.24, {
            x: 1,
            y: 1,
            ease: "back.out(1)",
            onComplete: () => {}
        }), 0);
    }

    _onBegin() {
        this._defaultTween = gsap.timeline();
        this.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._boss.position, 1, {
            x: common.STAGE_WIDTH * 0.08,
            y: common.STAGE_HEIGHT * 0.2,
            ease: Back.easeInOut,
            delay: 0.5,
            onComplete: () => {}
        }), 0);

        this._defaultTween.add(gsap.to(this._ivandoe.position, 1.25, {
            x: -common.STAGE_WIDTH * 0.19,
            y: common.STAGE_HEIGHT * 0.2,
            ease: Power1.easeInOut,
            delay: 0.5,
            onStart: () => this._ivandoe.signals.enter.dispatch(),
            onComplete: () => {
                this._hud.attackBar.animateIn().then(() => {
                    this._hud.attackBar.enabled = true;
                    this._hud.surrenderButton.enabled = true;
                });
                this._zoomInToFocus().then();
                this._ivandoe.playIdleAnimation();
            }
        }), 0);

        this._defaultTween.add(gsap.to(this._bert.position, 1, {
            x: -common.STAGE_WIDTH * 0.27,
            y: common.STAGE_HEIGHT * 0.11,
            ease: Back.easeInOut,
            delay: 0.5,
            onComplete: () => {}
        }), 0);
    }

    _onComplete() {
        this._bgm && this._bgm.stop();
        
        P3.audio.playSound("vo_iv_accept_quest");
        if (!common.saveData.settings.musicMute) {
            P3.audio.playSound("music_boss_defeated");
        }

        this._boss.state = definitions.BOSS_STATE.DEAD;
        this._hud.attackBar.animateOut().then(() => {
            this._ivandoe.playWinAnimation();

            this.delay(0.3).then(() => {
                this._hud.animateInWinScreen().then(() => {
                    this.delay(this._endTransitionDelay).then(() => this.signals.next.dispatch(true));
                });
            })
        })
    }

    _onFail() {
        this._hud.attackBar.animateOut().then(() => {
            this._ivandoe.playDeathAnimation();

            this.delay(0.3).then(() => {
                this._hud.animateInLoseScreen().then(() => {
                    this.delay(this._endTransitionDelay).then(() => {
                        this.signals.next.dispatch(false);
                    });
                });
            });
        });
    }
}